import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, type ButtonProps, Popover, Stack, Text } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { useUserAirlinesQuery } from "../../hooks/useUserAirlinesQuery";
import type { Airline } from "../../types";
import { AirlinePickerTrigger } from "./AirlinePickerTrigger";
import { AirlinePickerContent } from "./AirlinePickerContent";

type AirlinePickerProps = {
  value?: string;
  label?: string;
  placeholder?: string;
  hideSubmitButton?: boolean;
  onChange?: (airline: Airline) => void;
  onSubmit?: (airline: Airline) => void;
};

export const AirlinePicker = ({
  value,
  label,
  placeholder,
  hideSubmitButton,
  onChange,
  onSubmit,
}: AirlinePickerProps) => {
  const i18n = useI18n();
  const { data: airlines, isLoading } = useUserAirlinesQuery();

  const [airlineCode, setAirlineCode] = useState(value);
  const [active, setActive] = useState(false);

  const selectedAirline = useMemo(() => airlines?.find((a) => a.code === airlineCode), [airlines, airlineCode]);

  const handleChange = useCallback(
    (airline: Airline) => {
      setAirlineCode(airline.code);
      setActive(false);
      onChange?.(airline);
    },
    [setAirlineCode, setActive, onChange]
  );

  const handleSubmit = useCallback(() => {
    if (selectedAirline) onSubmit?.(selectedAirline);
  }, [selectedAirline, onSubmit]);

  useEffect(() => {
    setAirlineCode(value);
  }, [value]);

  return (
    <Stack gap={1}>
      <Text variant="emphasized_2">
        {label ?? i18n.trans(t("flights_airline_portal_superuser_overview_report_dropdown_label"))}
      </Text>
      <Stack direction="row">
        <Popover
          closeAriaLabel="Close popover"
          position="bottom-start"
          forcePosition={true}
          hideArrow={true}
          hideClose={true}
          keepMounted={false}
          size="stretch"
          fill
          active={active}
          onOpen={() => setActive(true)}
          onClose={() => setActive(false)}
        >
          <Popover.Trigger>
            {(attributes: ButtonProps["attributes"]) => (
              <AirlinePickerTrigger
                airlines={airlines}
                value={airlineCode}
                isLoading={isLoading}
                placeholder={placeholder}
                attributes={attributes}
              />
            )}
          </Popover.Trigger>
          <Popover.Content>
            <AirlinePickerContent airlines={airlines} value={airlineCode} onChange={handleChange} />
          </Popover.Content>
        </Popover>
        {!hideSubmitButton && (
          <Button variant="primary" size="large" onClick={handleSubmit}>
            {i18n.trans(t("flights_airline_portal_superuser_overview_report_dropdown_button"))}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
